/**
 * Centralized Subscription Guard Hook
 * Provides consistent subscription checking across all features
 *
 * COPIED FROM MOBILE PATTERN - matches checkSubscriptionAccess behavior
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/features/billing/contexts/SubscriptionContext';
import { useToast } from '@/shared/hooks/use-toast';

export type SubscriptionAccessReason =
  | 'subscribed'
  | 'trial'
  | 'paused'
  | 'canceled'
  | 'expired'
  | 'no_subscription'
  | 'loading';

export interface SubscriptionGuardResult {
  /**
   * Whether the user can access the feature
   * True if: subscribed OR (trial active AND trial type is 'app')
   */
  canAccess: boolean;

  /**
   * The reason for the access decision
   */
  reason: SubscriptionAccessReason;

  /**
   * Whether the user is currently loading subscription data
   */
  loading: boolean;

  /**
   * Whether access is blocked (paused or canceled subscription)
   */
  isBlocked: boolean;

  /**
   * Navigate to billing page with optional message
   */
  showUpgradeDialog: (message?: string) => void;

  /**
   * Check access and show appropriate error if denied
   * Returns true if access is granted, false otherwise
   */
  checkAccess: (featureName?: string) => boolean;
}

/**
 * Hook to check subscription access for features
 *
 * @example
 * ```tsx
 * const { canAccess, checkAccess, showUpgradeDialog } = useSubscriptionGuard();
 *
 * // Option 1: Check inline
 * if (!canAccess) {
 *   return <SubscriptionPrompt />;
 * }
 *
 * // Option 2: Check on action
 * const handleAction = () => {
 *   if (!checkAccess('Feature Name')) return;
 *   // perform action
 * }
 * ```
 */
export function useSubscriptionGuard(): SubscriptionGuardResult {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { usage, loading } = useSubscription();

  /**
   * Determine access reason based on subscription state
   */
  const reason: SubscriptionAccessReason = useMemo(() => {
    if (loading) return 'loading';

    // Check for blocked states first
    if (usage.is_paused) return 'paused';
    if (usage.subscription_status === 'canceled' || usage.subscriptionStatus === 'canceled') {
      return 'canceled';
    }
    if (usage.planType === 'expired') return 'expired';

    // Check for active access
    if (usage.subscribed) return 'subscribed';
    if (usage.isTrialActive && usage.trialType === 'app') return 'trial';

    // No active subscription or trial
    return 'no_subscription';
  }, [loading, usage]);

  /**
   * Can access if:
   * 1. User is subscribed (paid plan)
   * 2. User has an active app trial
   * 3. Still loading (optimistic - allow access while loading)
   *
   * IMPORTANT: This matches mobile app behavior
   */
  const canAccess = useMemo(() => {
    // OPTIMISTIC: Allow access while loading to prevent flash of "subscribe" message
    // Better UX - assume user has access until proven otherwise
    if (loading) return true;

    return (
      usage.subscribed ||
      (usage.isTrialActive && usage.trialType === 'app')
    );
  }, [loading, usage.subscribed, usage.isTrialActive, usage.trialType]);

  /**
   * Access is blocked if subscription is paused or canceled
   * Even if they technically have a subscription
   */
  const isBlocked = useMemo(() => {
    return (
      usage.is_paused ||
      usage.subscription_status === 'canceled' ||
      usage.subscriptionStatus === 'canceled'
    );
  }, [usage.is_paused, usage.subscription_status, usage.subscriptionStatus]);

  /**
   * Navigate to billing page with toast notification
   */
  const showUpgradeDialog = useCallback((message?: string) => {
    const defaultMessage = getDefaultMessage(reason);

    toast({
      title: getTitle(reason),
      description: message || defaultMessage,
      variant: 'destructive',
    });

    navigate('/profile?tab=billing');
  }, [navigate, toast, reason]);

  /**
   * Check access and show appropriate error if denied
   * Use this in action handlers (button clicks, form submits)
   */
  const checkAccess = useCallback((featureName?: string): boolean => {
    if (loading) {
      toast({
        title: 'Loading',
        description: 'Please wait while we load your subscription status...',
      });
      return false;
    }

    if (!canAccess) {
      const message = featureName
        ? `${featureName} requires an active subscription. Subscribe to continue.`
        : getDefaultMessage(reason);

      showUpgradeDialog(message);
      return false;
    }

    if (isBlocked) {
      const message = usage.is_paused
        ? `Your subscription is paused. Please reactivate to access ${featureName || 'this feature'}.`
        : `Your subscription has been canceled. Please resubscribe to access ${featureName || 'this feature'}.`;

      showUpgradeDialog(message);
      return false;
    }

    return true;
  }, [loading, canAccess, isBlocked, reason, showUpgradeDialog, usage.is_paused, toast]);

  return {
    canAccess,
    reason,
    loading,
    isBlocked,
    showUpgradeDialog,
    checkAccess,
  };
}

/**
 * Get title for toast notification based on reason
 */
function getTitle(reason: SubscriptionAccessReason): string {
  switch (reason) {
    case 'paused':
      return 'Subscription Paused';
    case 'canceled':
      return 'Subscription Canceled';
    case 'expired':
      return 'Subscription Expired';
    case 'no_subscription':
      return 'Subscription Required';
    default:
      return 'Subscribe to Access';
  }
}

/**
 * Get default message for toast notification based on reason
 */
function getDefaultMessage(reason: SubscriptionAccessReason): string {
  switch (reason) {
    case 'paused':
      return 'Your subscription is paused. Please reactivate to access this feature.';
    case 'canceled':
      return 'Your subscription has been canceled. Please resubscribe to access this feature.';
    case 'expired':
      return 'Your subscription has expired. Please renew to access this feature.';
    case 'no_subscription':
      return 'This feature requires an active subscription. Subscribe to continue.';
    case 'loading':
      return 'Loading subscription status...';
    default:
      return 'Subscribe to access this feature.';
  }
}
